import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { App } from "antd";

import { useSurveyViewForm } from "@/features/survey-views/form/hooks/useSurveyViewForm";
import { useExistingAnswerIds } from "@/features/survey-views/form/hooks/useExistingAnswerIds";
import {
  useGetSurveyViewByIdQuery,
  useUpdateSurveyViewMutation,
} from "@/redux/service";
import SurveyViewForm from "@/features/survey-views/form";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { SurveyViewFormValues } from "@/features/survey-views/form/types";
import {
  SurveyViewColorScheme,
  SurveyViewResponse,
} from "@/redux/survey-views/types";
import { SharingRights } from "@/shared/utils/constants";
import {
  hasStaleAnswerError,
  toFormAnswers,
} from "@/features/survey-views/form/utils";
import { handleErrors } from "@/shared/utils/forms";

const SurveyViewUpdate = () => {
  const { surveyViewUuid } = useParams();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { form } = useSurveyViewForm();
  const [updateSurveyView] = useUpdateSurveyViewMutation();
  const surveyViewResponse = useGetSurveyViewByIdQuery(
    surveyViewUuid as string,
  );
  const { existingAnswerIds, isReady, refetchAnswerIds } = useExistingAnswerIds(
    surveyViewResponse.data?.survey,
  );
  const [originalEvidenceIds, setOriginalEvidenceIds] = useState<number[]>([]);
  const hasLoadedRef = useRef(false);

  const loadIntoForm = useCallback(
    (surveyView: SurveyViewResponse, answerIds: Set<number> | null) => {
      const answers = toFormAnswers(surveyView.answers, answerIds);

      setOriginalEvidenceIds(
        answers.flatMap((answer) =>
          answer.attachedBlocks
            .filter((block) =>
              [
                SharingRights.PRME_COMMUNITY.value,
                SharingRights.PRIVATE.value,
              ].includes(block.sharingRights),
            )
            .map((block) => block.id),
        ),
      );

      form.reset({
        id: surveyViewUuid,
        title: surveyView.title,
        description: surveyView.description,
        survey: surveyView.survey,
        config: {
          ...surveyView.config,
          grayScale:
            surveyView.config.colorScheme === SurveyViewColorScheme.GRAY_SCALE,
        },
        answers,
      });
    },
    [form, surveyViewUuid],
  );

  useEffect(() => {
    // Both the view and the live answers are needed, otherwise every answer would
    // look deleted and be stripped from the form.
    if (!surveyViewResponse.data || !isReady) return;
    if (hasLoadedRef.current) return;

    hasLoadedRef.current = true;
    loadIntoForm(surveyViewResponse.data, existingAnswerIds);
  }, [surveyViewResponse.data, isReady, existingAnswerIds, loadIntoForm]);

  /**
   * Someone else removed an answer while this form was open. Pull both sides in
   * again and rebuild the form so the user can save without losing their edits
   * to the rest of the view.
   */
  const reloadAfterStaleAnswers = async () => {
    const [surveyView, answerIds] = await Promise.all([
      surveyViewResponse.refetch().unwrap(),
      refetchAnswerIds(),
    ]);

    loadIntoForm(surveyView, answerIds);
    notification.warning({
      message: t("surveyViews.errors.answersChanged"),
      description: t("surveyViews.errors.answersChangedDescription"),
    });
  };

  const handleSubmit = async (values: SurveyViewFormValues) => {
    if (!surveyViewUuid) return;

    const data = {
      ...values,
      config: {
        ...values.config,
        colorScheme: values.config.grayScale
          ? SurveyViewColorScheme.GRAY_SCALE
          : SurveyViewColorScheme.DEFAULT,
      },
      answers: values.answers.map((answer) => ({
        answer: answer.answer,
        attachedBlocks: answer.attachedBlocks.map((block) => block.id),
      })),
    };

    try {
      const response = await updateSurveyView({
        id: surveyViewUuid,
        data,
      }).unwrap();
      notification.success({
        message: t("surveyViews.labels.surveyViewUpdated"),
      });
      navigate(`/reports/views/${response.id}`);
      form.reset();
    } catch (error) {
      if (hasStaleAnswerError(error)) {
        await reloadAfterStaleAnswers();
        return;
      }
      handleErrors(
        error as Parameters<typeof handleErrors>[0],
        form.setError,
        notification,
        t,
      );
    }
  };

  if (surveyViewResponse.isLoading || !isReady) {
    return <BaseSpinner />;
  }

  return (
    <SurveyViewForm
      form={form}
      handleSubmit={form.handleSubmit(handleSubmit)}
      originalEvidenceIds={originalEvidenceIds}
    />
  );
};

export default SurveyViewUpdate;
